import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SecurityContext,
  ViewEncapsulation,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import {
  BackgroundColor,
  ImageOverlayStyle,
  CardsCarouselCard,
  CtaButton,
  InlineImage,
  CarouselCardType,
  SvgIconClass,
} from '@digital-platform/rs-cms/domain';
import { IconComponent } from '@digital-platform/rs-shared/ui-brand';
import { BasePTypeComponent } from '../base-ptype/base-ptype.component';
import { CtaButtonComponent } from '../cta-button/cta-button.component';
import { InlineImageComponent } from '../inline-image/inline-image.component';
import { RichTextComponent } from '../rich-text/rich-text.component';

interface CardsCarouselCardAngularConfig {
  hideOnMobile?: boolean;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    InlineImageComponent,
    RichTextComponent,
    CtaButtonComponent,
    IconComponent,
  ],
  selector: 'digital-platform-cards-carousel-card',
  templateUrl: './cards-carousel-card.component.html',
  styleUrls: ['./cards-carousel-card.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CardsCarouselCardComponent
  extends BasePTypeComponent
  implements CardsCarouselCard, OnInit, OnChanges
{
  @Input() public carouselCardType: CarouselCardType | null = {
    tid: 0,
    name: '',
    colorClass: '',
  };
  @Input() public bgColor: BackgroundColor | null = null;
  @Input() public ctaButton: CtaButton[] | null = [];
  @Input() public inlineImage: InlineImage[] | null = [];
  @Input() public richTextTitle: string | null = '';
  @Input() public svgIconClass: SvgIconClass | null = {
    tid: 0,
    name: '',
    iconClass: '',
  };
  @Input() public angularConfig?: CardsCarouselCardAngularConfig | string;

  public override type = 'cardsCarouselCard' as const;
  public imageContainerContentClasses = '';
  public excludeImgOverrideAttrs: string[] = [];
  public esServicesAvailable = true;
  public defaultCornerObject: ImageOverlayStyle = {
    tid: 6991,
    name: 'Rounded Corner Bottom Left',
    overlayStyle: 'rounded-corner-sw',
  };
  public cardClass: ImageOverlayStyle | null = null;
  public cardColor: BackgroundColor | null = null;
  public styleAngularConfig: CardsCarouselCardAngularConfig | null = null;

  constructor(public sanitizer: DomSanitizer) {
    super();
  }

  public ngOnInit(): void {
    if (this.carouselCardType) {
      this.esServicesAvailable = false;
    }

    /* By design certain values are hardcoded for when a carouselCardType is given */
    if (this.carouselCardType?.name) {
      if (this.inlineImage) {
        this.inlineImage[0].roundedCornerRadius = '96.00';
      }

      const newObject = JSON.parse(JSON.stringify(this.carouselCardType));
      newObject['cssColorName'] = newObject['colorClass'];
      delete newObject['colorClass'];

      this.cardColor = newObject;
      this.excludeImgOverrideAttrs = ['imageOverlayColor'];
      this.cardClass = this.defaultCornerObject;
    } else if (this.inlineImage?.[0]) {
      if (this.inlineImage[0].imageOverlayStyle) {
        this.cardClass = this.inlineImage[0].imageOverlayStyle;
      }
      this.cardColor = this.inlineImage[0].imageOverlayColor;
    }
  }

  public ngOnChanges() {
    this.imageContainerContentClasses = this.initImageContainerContentClasses();

    if (typeof this.angularConfig === 'string') {
      this.styleAngularConfig = JSON.parse(this.angularConfig);
    } else if (typeof this.angularConfig === 'object') {
      this.styleAngularConfig = this.angularConfig;
    }
  }

  public get iconVisibilityClass(): string {
    return this.styleAngularConfig?.hideOnMobile ? 'hide-on-mobile' : '';
  }

  public stringOrNullToString(cssClass: string | null = ''): string {
    const retVal: string = cssClass || '';
    return this.sanitizer.sanitize(SecurityContext.NONE, retVal) || '';
  }

  private initImageContainerContentClasses(): string {
    if (this.bgColor?.cssColorName) {
      return this.bgColor?.cssColorName + ' has-bg-color';
    }
    return '';
  }
}
